import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { FileText, Users, LayoutDashboard, Loader2 } from "lucide-react";

const TILES = [
  {
    key: "rechnung",
    title: "Rechnung in Sekunden",
    desc: "Erstelle eine professionelle Rechnung mit sofortiger PDF-Vorschau.",
    icon: FileText,
    to: "/invoices?create=1",
  },
  {
    key: "kunde",
    title: "Kunde & Projekt anlegen",
    desc: "Lege Auftraggeber und Projekte an — die Basis für deine Rechnungen.",
    icon: Users,
    to: "/customers",
  },
  {
    key: "dashboard",
    title: "Dashboard ansehen",
    desc: "Umsatz, Ausgaben und Steuer-Rücklage an realistischen Beispielzahlen.",
    icon: LayoutDashboard,
    to: "/dashboard",
  },
];

export default function GuestWelcome() {
  const { user, switchGuestCountry, switchGuestPersona } = useAuth();
  const navigate = useNavigate();
  const [switching, setSwitching] = useState(false);
  const country = user?.country === "AT" ? "AT" : "DE";
  const persona = ["freelancer", "kleinunternehmer", "agentur"].includes(user?.demo_persona)
    ? user.demo_persona
    : "freelancer";

  const kuRef = country === "AT" ? "§ 6 Abs. 1 Z 27 UStG" : "§ 19 UStG";
  const PERSONAS = [
    { key: "freelancer", label: "Freelancer", hint: "Solo-Selbstständig, mit USt" },
    { key: "kleinunternehmer", label: "Kleinunternehmer", hint: `${kuRef}, ohne USt` },
    { key: "agentur", label: "Agentur", hint: "Team & größere Projekte" },
  ];

  const changeCountry = async (next) => {
    if (next === country || switching) return;
    setSwitching(true);
    try {
      await switchGuestCountry(next);
      toast.success(next === "AT" ? "Länderkontext: Österreich" : "Länderkontext: Deutschland");
    } catch {
      toast.error("Länderwechsel fehlgeschlagen.");
    } finally {
      setSwitching(false);
    }
  };

  const changePersona = async (next) => {
    if (next === persona || switching) return;
    setSwitching(true);
    try {
      const p = PERSONAS.find((x) => x.key === next);
      await switchGuestPersona(next);
      toast.success(`Beispieldaten: ${p ? p.label : next}`);
    } catch {
      toast.error("Persona-Wechsel fehlgeschlagen.");
    } finally {
      setSwitching(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto min-h-[72vh] flex flex-col justify-center" data-testid="guest-welcome">
      <div className="text-center mb-8">
        <h1 className="font-heading text-3xl sm:text-4xl font-bold text-slate-900">
          Was möchtest du zuerst sehen?
        </h1>
        <p className="mt-3 text-slate-600">
          Schau dich in Ruhe an den Beispieldaten um oder leg direkt eigene Inhalte an — ganz ohne Anmeldung.
        </p>
      </div>

      {/* Persona-Umschalter: passende Beispielzahlen je Geschäftstyp */}
      <div className="mb-6">
        <p className="text-center text-sm text-slate-500 mb-3">Zeig mir Beispieldaten als …</p>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3" data-testid="guest-persona-switch">
          {PERSONAS.map((p) => (
            <button
              key={p.key}
              onClick={() => changePersona(p.key)}
              disabled={switching}
              data-testid={`guest-persona-${p.key}`}
              className={`rounded-xl border p-3 text-left transition-all ${
                persona === p.key
                  ? "border-emerald-500 bg-emerald-50 ring-1 ring-emerald-500"
                  : "border-slate-200 bg-white hover:border-emerald-300"
              }`}
            >
              <span className="block font-semibold text-slate-900 text-sm">{p.label}</span>
              <span className="block text-xs text-slate-500 mt-0.5">{p.hint}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Länder-Umschalter DE / AT */}
      <div className="flex items-center justify-center gap-2 mb-8">
        <span className="text-sm text-slate-500 mr-1">Länderkontext:</span>
        <div className="inline-flex rounded-full border border-slate-200 bg-white p-1" data-testid="guest-country-switch">
          {["DE", "AT"].map((c) => (
            <button
              key={c}
              onClick={() => changeCountry(c)}
              disabled={switching}
              data-testid={`guest-country-${c}`}
              className={`px-4 py-1.5 rounded-full text-sm font-medium transition-colors ${
                country === c ? "bg-emerald-600 text-white" : "text-slate-600 hover:bg-slate-50"
              }`}
            >
              {c === "DE" ? "🇩🇪 Deutschland" : "🇦🇹 Österreich"}
            </button>
          ))}
        </div>
        {switching && <Loader2 className="h-4 w-4 animate-spin text-emerald-600" />}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
        {TILES.map((tile, idx) => {
          const Icon = tile.icon;
          return (
            <button
              key={tile.key}
              onClick={() => navigate(tile.to)}
              data-testid={`guest-tile-${tile.key}`}
              className="group text-left rounded-2xl border border-slate-200 bg-white p-6 hover:border-emerald-300 hover:shadow-lg transition-all duration-200"
              style={{ animationDelay: `${idx * 60}ms` }}
            >
              <div className="w-12 h-12 rounded-xl bg-emerald-50 flex items-center justify-center mb-4 group-hover:bg-emerald-100 transition-colors">
                <Icon className="h-6 w-6 text-emerald-600" />
              </div>
              <h3 className="font-semibold text-slate-900 mb-1">{tile.title}</h3>
              <p className="text-sm text-slate-500 leading-relaxed">{tile.desc}</p>
            </button>
          );
        })}
      </div>

      <div className="text-center mt-8">
        <Button
          variant="ghost"
          onClick={() => navigate("/dashboard")}
          className="text-slate-500 hover:text-slate-700"
          data-testid="guest-welcome-explore"
        >
          Oder einfach frei umschauen →
        </Button>
      </div>
    </div>
  );
}
